import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createGlobalStyle } from 'styled-components';
import Recaptcha from 'reaptcha';
import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import { fetchCountryCode } from 'geo_util';
import { default as ErrorTracker, captureMessage } from 'error_tracker';

import ResponsiveProvider from 'core/providers/ResponsiveProvider';
import ErrorBoundary from 'core/components/ErrorBoundary';
import ApiRequest from 'core/api/request';
import FB from 'core/utils/facebook';
import GUtil from 'core/utils/google';
import { saveCookie, checkCookie } from 'core/utils/loginCookies';
import { HeapUtil, promiser, gaEvent, trackFbEvent } from 'core/utils';

const HideRecaptchaBadgeStyle = createGlobalStyle`
	.grecaptcha-badge {
		display: none !important;
	}
`;


class LoginContainer extends Component {
	/**
	* There could be 1+ instances of this component on a page
	* And we only ever need 1 instance of this listener / loader
	*/
	static loginIntentListener = false;

	state = {
		outsideUsa: false,
		signInError: false,
		forgotError: false,
		forgotSuccess: false,
		loading: false,
		captchaReady: false,
		createAccountError: false,
		createAccountErrorCode: '',
		createAccountMsg: '',
		accountCookied: false,
	}

	clearErrors = (errorType) => {
		if (errorType === 'createAccount') {
			this.setState({
				createAccountError: false,
				createAccountErrorCode: '',
			});
			return;
		} else if (errorType === 'signIn') {
			// Forgot and SignIn use the same input labels
			this.setState({
				signInError: false,
				forgotError: false,
			});
			return;
		}

		this.setState({
			createAccountError: false,
			createAccountErrorCode: '',
			signInError: false,
			forgotError: false,
		});
	}

	// We will use these 3 to move data internally
	// for captcha reasons and to use the token with our backend
	// TODO: Refactor this into state? We shouldn't hang data on the
	// component like this, but this is old and this component is getting
	// some TLC soon with multicart implementation, so we'll leave it for now.
	captcha = null;
	data = {};
	url = '';

	// Will set true if form submission happens before script is ready
	waitingForCaptcha = false;

	forgotHandler = email => {
		this.setState({ loading: true });
		const { contextUsage } = this.props;

		const data = { email: email };
		ApiRequest.post('/customer/forgot', data)
			.then(({ email, message }) => {
				Stats.trackGeneric(contextUsage, {
					step_name: 'bubble_forgot'
				});

				Stats.trackGeneric('login-forgot', {
					step_name: contextUsage,
				});
				this.setState({
					forgotSuccess: message === 'ok',
					forgotError: message !== 'ok',
					loading: false,
				});
			})
			.catch(e => {
				const error = e["errors"][0];
				Stats.trackGeneric('mp_error', {
					step_name: 'bubble_forgot',
					error_message: error.message,
					error_code: error.error_code
				});
				Stats.trackGeneric('login-forgot-error', {
					step_name: contextUsage,
					error_message: error.message,
					error_code: error.error_code
				});
				this.setState({ forgotError: true, loading: false });
			});
	}

	verifyCallback = recaptchaToken => {
		const { data, url } = this;
		const { contextUsage, onGuestCheckoutSelected } = this.props;

		const fullData = {
			...data,
			recaptchaToken,
		};

		// Prevent callbacks from other places
		if (!url) return;
		const isLogin = url === '/customer/login';
		const isCreate = url === '/customer/register';
		const isGuest = url === '/customer/register_guest';

		const mode = isCreate ? 'register' : 'login';

		if (isGuest) {
			// Guest registration happens automatically right before the order is placed
			return onGuestCheckoutSelected();
		}

		ApiRequest.post(url, fullData)
			.then(res => {
				// make user data available for SPAs
				window.cj.user = res;
				saveCookie();

				Stats.trackGeneric(mode, {
					step_name: contextUsage,
				});

				trackFbEvent('Lead');
				gaEvent(mode, contextUsage);

				HeapUtil.identify();

				ErrorTracker.setUser({
					email: res.email,
					id: res.id,
				});

				FullstoryUtil.identify(
					res.id,
					res.name,
					res.email,
				);

				if(window && window.gtag) {
					// Adwords tag for login or sign-up
					window.gtag('event', 'conversion', {
						'send_to': 'AW-957505549/SFEpCO7Gi8kBEI3AycgD',
					});
					window.gtag('event', 'logged in', {
						'event_category': 'auth',
						'event_label': '' + res.id
					});
				}

				// Login success, hit up that callback
				this.props.onLoginSuccess(res);
			})
			.catch(e => {
				FullstoryUtil.sendEvent("loginError");
				const error = e["errors"][0];
				Stats.trackGeneric('mp_error', {
					step_name: 'bubble_' + mode,
					error_message: error.message,
					error_code: error.error_code
				});
				Stats.trackGeneric(`${mode}-error`, {
					step_name: contextUsage,
					error_message: error.message,
					error_code: error.error_code
				});
				if (isLogin || isGuest) {
					this.setState({ signInError: true, loading: false });
				}

				if (isCreate) {
					this.setState({
						createAccountError: true,
						createAccountErrorCode: error.error_code,
						createAccountMsg: error.message,
						loading: false,
					});
				}

				this.url = '';
			});
	};

	completeLogin = async () => {
		if (!this.useRecaptcha()) {
			this.verifyCallback('faketoken');
			return;
		}

		const { captcha } = this;
		const [e1, r1] = await promiser(captcha.reset());
		const [e2, r2] = await promiser(captcha.execute());
		if (e1 || e2) {
			// Let recaptchaLoadCallback know to finish the job...
			this.waitingForCaptcha = true;
			// Catch the promise rejection in case recaptcha isn't loaded yet
			// And report to Statjoy + Fullstory
			const stepName = this.url.split("/").pop(); // login|register|guest
			Stats.trackGeneric('recaptcha_unavailable', { step_name: stepName });
			FullstoryUtil.sendEvent('Waiting for Recaptcha');
		}
	}

	signInHandler = (email, signInPassword) => {
		this.setState({ loading: true });

		this.resetStateTimer();

		this.data = {
			email: email,
			password: signInPassword,
		};
		this.url = '/customer/login';

		this.completeLogin();
	}

	createAccountHandler = (email, createAccountPassword, fullName, wantsPromotions) => {
		this.setState({ loading: true });

		this.resetStateTimer();

		const { outsideUsa } = this.state;
		// Data for the verifyCallback
		this.data = {
			name: fullName,
			email: email,
			password: createAccountPassword,
			// Free choice if outsideUsa email tax if inside
			emailOptInStatus: outsideUsa
				? wantsPromotions
				: true,
		};
		this.url = '/customer/register';

		this.completeLogin();
	}

	// To be used by getLoginStatus on FacebookUtil
	fbAuthResponse = undefined;

	facebookClick = event => {
		event.preventDefault();
		const { contextUsage } = this.props;

		Stats.trackGeneric(contextUsage, {
			step_name: 'bubble_facebook-sso'
		});

		Stats.trackGeneric('login-facebook', {
			step_name: contextUsage
		});

		trackFbEvent('Lead');
		this.props.onBeforeSocialLogin();

		if (this.fbAuthResponse) {
			FB.postConnectionLogin(this.fbAuthResponse);
		} else {
			FB.login();
		}

		return false;
	}

	googleClick = event => {
		event.preventDefault();
		const { contextUsage } = this.props;
		Stats.trackGeneric(contextUsage, {
			step_name: 'bubble_google-sso'
		});

		Stats.trackGeneric('login-google', {
			step_name: contextUsage
		});

		trackFbEvent('Lead');
		this.props.onBeforeSocialLogin();

		GUtil.login();

		return false;
	}

	// To show or hide the newsletter checkbox via outsideUsa state prop
	corroborateFreedom = () => {
		fetchCountryCode()
			.then(country_code => {
				const outsideUsa = country_code !== 'US';
				if (outsideUsa !== this.state.outsideUsa) {
					this.setState({ outsideUsa });
				}
			})
			.catch(error => {
				captureMessage(`Error on fetchCountryCode: ${error}`);
			});
	}

	recaptchaLoadCallback = () => {
		this.setState({ captchaReady: true }, () => {
			if (this.waitingForCaptcha) {
				this.completeLogin();
			}
		});
	}

	checkCookie = () => {
		const accountCookied = checkCookie();
		if (accountCookied) this.setState({ accountCookied: true });
	}

	/**
	* Inject recaptcha script manually
	* to give us control over how/when to load
	*/
	injectScript = () => {
		const _inject = () => {
			const _id = 'cj-recaptcha-api';
			// Avoid loading script twice
			if (document.getElementById(_id)) return;
			const src = 'https://recaptcha.net/recaptcha/api.js?render=explicit';
			const script = document.createElement('script');

			script.id = _id;
			script.async = true;
			script.defer = true;
			script.src = src;

			if (document.head) {
				document.head.appendChild(script);
			}
		};
		// 1. Do setTimeout for 1s
		// 2. Wrap call in `requestIdleCallback`, if available
		// 3. Then load the script
		setTimeout(() => {
			"requestIdleCallback" in window
				? requestIdleCallback(_inject, { timeout: 1000 })
				: _inject();
		}, 0);
	}

	useRecaptcha () {
		const skipMode = (window.cj.skipMode || []).includes("recaptcha");
		if ((window.cj.fastMode || skipMode) && window.cj.useRecaptcha) {
			console.debug("Login will NOT work with useRecaptcha & fastMode/skipMode both true!");
		}
		return window.cj.useRecaptcha && !window.cj.fastMode && !skipMode;
	}

	componentDidMount () {
		if (window.FB) FB.getLoginStatus(this);
		this.checkCookie();
		this.corroborateFreedom();
		if (this.useRecaptcha()) {
			this.attachLoginEvents();
		} else {
			// this needs to fire so the form buttons are enabled
			this.recaptchaLoadCallback();
		}
	}

	componentWillUnmount () {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	attachLoginEvents = () => {
		// Check static prop so we only attach 1 instance of this listener
		if (LoginContainer.loginIntentListener) return;
		LoginContainer.loginIntentListener = true;

		// Manual script injection only when theres a login-intent
		if (window.cjLoginIntentReady) {
			this.injectScript();
		} else {
			window.addEventListener('loginIntent', (e) => {
				if (document.readyState === "complete") {
					// ready so inject now
					this.injectScript();
				} else {
					// wait until load event
					window.addEventListener('load', this.injectScript, { once: true });
				}
			}, { once: true });
		}
	}

	resetStateTimer = () => {
		// Little timeout hack to get out loading state incase recaptcha is closed early
		this.timeout = setTimeout(() => {
			this.timeout = null;
			this.setState({ loading: false });
		}, 5000);
	}

	onGuestCheckoutSelected = async () => {
		this.setState({ loading: true });

		this.resetStateTimer();

		this.url = '/customer/register_guest';

		this.completeLogin();
	}

	render () {
		const { children } = this.props;
		const sitekey = window.cj.googleRecaptchaSiteKey;
		const {
			verifyCallback,
			recaptchaLoadCallback,
		} = this;

		const childProps = {
			signInHandler: this.signInHandler,
			forgotHandler: this.forgotHandler,
			createAccountHandler: this.createAccountHandler,
			facebookClick: this.facebookClick,
			googleClick: this.googleClick,
			clearErrors: this.clearErrors,
			onGuestCheckoutSelected: this.onGuestCheckoutSelected,
			setLoading: (loading) => {
				this.setState({ loading: loading }); 
			},
			...this.state,
		};

		return (
			<ErrorBoundary>
				<ResponsiveProvider>
					{ children(childProps) }
					<Recaptcha
						sitekey={sitekey}
						size="invisible"
						onLoad={recaptchaLoadCallback}
						onVerify={verifyCallback}
						ref={e => (this.captcha = e)}
						inject={false/* do this manually */}
					/>
					<HideRecaptchaBadgeStyle />
				</ResponsiveProvider>
			</ErrorBoundary>
		);
	}

	static propTypes = {
		children: PropTypes.func.isRequired,
		onBeforeSocialLogin: PropTypes.func,
		onLoginSuccess: PropTypes.func.isRequired,
		contextUsage: PropTypes.string,
		onGuestCheckoutSelected: PropTypes.func,
	}

	static defaultProps = {
		onBeforeSocialLogin: () => null,
		onLoginSuccess: () => {
			if (window.cj.back_url) {
				window.location = window.cj.back_url;
			} else {
				window.location.reload(true);
			}
		},
		contextUsage: 'standard-login-popup',
		onGuestCheckoutSelected: () => {},
	}
}

export default LoginContainer;
